import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, CircularProgress, Container, CssBaseline, Divider, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import Header from '../components/Header/header';
import { useConnection } from '../connection/Application';
import { paperCrumbs, readBody } from '../components/Cba/cbaApi';
import usePaperDetail from '../components/Cba/usePaperDetail';
import { ExamResult, percentageLine, scoreLine } from '../components/Cba/resultView';
import {
  missedCount,
  onlyMissed,
  OptionState,
  optionState,
  ReviewOption,
  ReviewQuestion,
  verdict,
} from '../components/Cba/reviewView';

interface ReviewPayload extends ExamResult {
  questions: ReviewQuestion[];
}

/** How each option is drawn once the paper is marked. */
const OPTION_STYLE: Record<OptionState, { border: string; background: string; note?: string }> = {
  'chosen-correct': { border: '#0F5C55', background: '#EAF2F0', note: 'Your answer · correct' },
  'chosen-wrong': { border: '#A32A1E', background: '#FBEDEB', note: 'Your answer' },
  correct: { border: '#0F5C55', background: '#EAF2F0', note: 'Correct answer' },
  plain: { border: '#D3DAD5', background: 'transparent' },
};

const VERDICT_COLOUR: Record<string, 'success' | 'error' | 'warning'> = {
  Correct: 'success',
  Incorrect: 'error',
  'Not answered': 'warning',
};

const Content: FC<{ text?: string; contentType?: string; image?: string }> = ({ text, contentType, image }) => (
  <Box>
    {!!text && contentType === 'richText' ? (
      <Box sx={{ '& p': { margin: 0 } }}>{ReactHtmlParser(text)}</Box>
    ) : (
      !!text && <Typography variant='body1' component='span'>{text}</Typography>
    )}
    {!!image && (
      <Box component='img' src={image} alt='' sx={{ display: 'block', maxWidth: '100%', mt: text ? 1 : 0, borderRadius: 1 }} />
    )}
  </Box>
);

/**
 * The completed paper with the answers shown.
 *
 * This is the only place a correct answer reaches a student, and only once the
 * paper is marked and closed. Every option is drawn, with the student's choice
 * and the correct one both marked, so a question is worth rereading even where
 * the answer was wrong.
 */
const CbaReview: FC = () => {
  const { attemptId } = useParams();
  const connection = useConnection();
  const navigate = useNavigate();

  const [review, setReview] = useState<ReviewPayload>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [missedOnly, setMissedOnly] = useState(false);

  const fetchReview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await connection.get(`cba/attempts/${attemptId}/review`);
      setReview(readBody<ReviewPayload>(response));
    } catch (e: any) {
      const message = typeof e?.message === 'string' ? e.message : '';
      setError(message !== '' ? message : 'This paper could not be loaded for review.');
    } finally {
      setLoading(false);
    }
  }, [attemptId, connection]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  const questions = review?.questions ?? [];
  const shown = missedOnly ? onlyMissed(questions) : questions;

  const paperId = review?.paper?.id;
  const paperDetail = usePaperDetail(paperId);
  const crumbs = paperCrumbs(
    paperDetail,
    review?.paper?.title ?? '',
    `/cba/papers/${paperId ?? ''}`,
    { label: 'Result', href: `/cba/attempts/${attemptId}/result` },
    { label: 'Review', href: `/cba/attempts/${attemptId}/review` },
  );

  return (
    <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
      <CssBaseline />
      <Header x={{ crumbs }} />

      <Box sx={{ pt: { xs: 12, md: 14 }, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : error !== '' || !review ? (
          <>
            <Alert severity='error'>{error || 'This paper could not be loaded for review.'}</Alert>
            <Button sx={{ mt: 2 }} onClick={() => navigate('/cba')}>
              Back to exam papers
            </Button>
          </>
        ) : (
          <>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px #0000001A' }}>
              <Typography variant='h5' sx={{ color: '#C44A1E', fontWeight: 500 }}>
                {review.paper?.title ?? 'Exam paper'}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                Attempt {review.attempt.attemptNo} · {scoreLine(review)} marks ·{' '}
                {percentageLine(review)}
              </Typography>

              <Stack
                direction='row'
                spacing={2}
                sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
                alignItems='center'>
                <FormControlLabel
                  control={
                    <Switch
                      checked={missedOnly}
                      onChange={(event) => setMissedOnly(event.target.checked)}
                    />
                  }
                  label={`Show only what I got wrong (${missedCount(questions)})`}
                />
                <Box sx={{ flex: 1 }} />
                <Button
                  variant='outlined'
                  onClick={() => navigate(`/cba/attempts/${attemptId}/result`)}>
                  Back to result
                </Button>
              </Stack>
            </Card>

            {shown.length === 0 ? (
              <Alert severity='success' sx={{ mt: 3 }}>
                You answered every question correctly on this paper.
              </Alert>
            ) : (
              <Stack spacing={2} sx={{ mt: 3 }}>
                {shown.map((question) => {
                  const outcome = verdict(question);

                  return (
                    <Card
                      key={question.questionId}
                      sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px #0000001A' }}>
                      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
                        <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>
                          Question {question.questionNumber}
                        </Typography>
                        <Chip size='small' label={outcome} color={VERDICT_COLOUR[outcome]} />
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                          {question.marksAwarded} of {question.marks} mark
                          {question.marks === 1 ? '' : 's'}
                        </Typography>
                      </Stack>

                      <Content
                        text={question.question?.question}
                        contentType={question.question?.questionContentType}
                        image={question.question?.questionImage}
                      />

                      <Divider sx={{ my: 2 }} />

                      <Stack spacing={1}>
                        {(question.question?.answer ?? []).map((option: ReviewOption) => {
                          const state = optionState(option, question);
                          const style = OPTION_STYLE[state];

                          return (
                            <Box
                              key={option.value}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: style.border,
                                background: style.background,
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                              }}>
                              <Content
                                text={option.answer}
                                contentType={option.answerContentType}
                                image={option.answerImage}
                              />
                              {style.note && (
                                <Typography
                                  variant='caption'
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    fontWeight: 600,
                                    color: style.border,
                                  }}>
                                  {style.note}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>

                      {!question.answered && (
                        <Typography
                          variant='caption'
                          sx={{ color: '#8A5A12', display: 'block', mt: 1.5 }}>
                          You left this question blank.
                        </Typography>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default CbaReview;
